/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import {
  WebSocketConnectionState,
  WebSocketError,
  WebSocketErrorCode,
  TransportEventType
} from "./types";
import type {
  TransportEvent,
  TransportEventListener,
  IOutboundBufferEntry
} from "./types";

/**
 * Browser WebSocket transport layer using the native WebSocket API.
 *
 * **REQ-WEB-TRANS-01:** Exclusively `wss://` connections.
 * **REQ-TRANS-03:** Emits typed transport events (Open, TextMessage, Error, Closed).
 * **REQ-TRANS-04:** `send(message)` with error on non-connected state.
 * **REQ-TRANS-05:** Graceful close with configurable close code.
 * **REQ-TRANS-07:** Synchronous connection state.
 * **REQ-TRANS-08:** Maximum inbound message size enforcement (1024 bytes).
 * **REQ-TRANS-09:** Outbound ring buffer (8 slots).
 * **REQ-TRANS-11:** Close guard with typed close codes.
 */
export class WebSocketTransport {
  private static readonly TAG: string = "WebSocketTransport";

  private static readonly DEFAULT_MAX_INBOUND_SIZE: number = 1024;
  private static readonly DEFAULT_BUFFER_CAPACITY: number = 8;

  /** Close codes per REQ-TRANS-11. */
  private static readonly CLOSE_ALREADY_DISCONNECTING: number = 3004;
  private static readonly CLOSE_FORCE_CANCEL: number = 3005;
  private static readonly CLOSE_NO_ACTIVE_CONNECTION: number = 3006;
  private static readonly CLOSE_ABNORMAL: number = 3002;
  private static readonly CLOSE_PEER_INITIATED: number = 3001;

  private mSocket: WebSocket | null = null;
  private mState: WebSocketConnectionState =
    WebSocketConnectionState.DISCONNECTED;
  private mEventListeners: TransportEventListener[] = [];

  /** Outbound ring buffer (REQ-TRANS-09). */
  private mOutboundBuffer: Array<IOutboundBufferEntry | null>;
  private mBufferHead: number = 0;
  private mBufferTail: number = 0;
  private mBufferCount: number = 0;
  private mIsDrainScheduled: boolean = false;

  private readonly mMaxInboundSize: number;
  private readonly mBufferCapacity: number;

  constructor(
    maxInboundSize: number = WebSocketTransport.DEFAULT_MAX_INBOUND_SIZE,
    bufferCapacity: number = WebSocketTransport.DEFAULT_BUFFER_CAPACITY
  ) {
    this.mMaxInboundSize = maxInboundSize;
    this.mBufferCapacity = bufferCapacity;
    this.mOutboundBuffer = new Array<IOutboundBufferEntry | null>(
      this.mBufferCapacity
    ).fill(null);
  }

  private attachSocketListeners(): void {
    if (this.mSocket === null) {
      return;
    }

    this.mSocket.onopen = (): void => {
      Log.d(WebSocketTransport.TAG, "onopen(): WebSocket connection opened");
      this.mState = WebSocketConnectionState.CONNECTED;

      // Browser WebSocket API does not expose upgrade response headers,
      // so the ping interval cannot be read from x-tivo-ping-interval.
      // The PingManager will use the default interval.
      this.emitEvent({
        type: TransportEventType.Open,
        protocol:
          this.mSocket !== null ? this.mSocket.protocol : "com.tivo.mindrpc.2",
        pingIntervalSeconds: 0 // 0 signals "use default"
      });
    };

    this.mSocket.onmessage = (event: MessageEvent): void => {
      if (typeof event.data === "string") {
        // REQ-TRANS-08: Maximum inbound message size enforcement
        const byteLength: number = new TextEncoder().encode(
          event.data
        ).byteLength;
        if (byteLength > this.mMaxInboundSize) {
          Log.w(
            WebSocketTransport.TAG,
            `onmessage(): Message too large (${byteLength} bytes > ${this.mMaxInboundSize} limit), rejecting`
          );
          this.emitEvent({
            type: TransportEventType.Error,
            code: WebSocketErrorCode.MESSAGE_TOO_LARGE,
            detail: `Inbound message exceeds maximum size: ${byteLength} > ${this.mMaxInboundSize}`
          });
          return;
        }

        this.emitEvent({
          type: TransportEventType.TextMessage,
          data: event.data
        });
      } else if (event.data instanceof ArrayBuffer) {
        if (event.data.byteLength > this.mMaxInboundSize) {
          Log.w(
            WebSocketTransport.TAG,
            `onmessage(): Binary message too large (${event.data.byteLength} bytes), rejecting`
          );
          this.emitEvent({
            type: TransportEventType.Error,
            code: WebSocketErrorCode.MESSAGE_TOO_LARGE,
            detail: `Inbound binary message exceeds maximum size: ${event.data.byteLength} > ${this.mMaxInboundSize}`
          });
          return;
        }

        this.emitEvent({
          type: TransportEventType.BinaryMessage,
          data: event.data,
          length: event.data.byteLength
        });
      }
    };

    this.mSocket.onerror = (): void => {
      Log.e(
        WebSocketTransport.TAG,
        "onerror(): WebSocket error event received"
      );
      this.emitEvent({
        type: TransportEventType.Error,
        code: WebSocketErrorCode.TRANSPORT_ERROR,
        detail: "WebSocket error event"
      });
    };

    this.mSocket.onclose = (event: CloseEvent): void => {
      Log.d(
        WebSocketTransport.TAG,
        `onclose(): code=${event.code}, reason="${event.reason}", wasClean=${event.wasClean}`
      );

      // Save previous state before transitioning — needed to distinguish
      // client-initiated close (CLOSING) vs server/peer-initiated close.
      const previousState: WebSocketConnectionState = this.mState;
      this.mState = WebSocketConnectionState.DISCONNECTED;
      this.resetBuffer();

      if (!event.wasClean && event.code === 1006) {
        // REQ-TRANS-11: Abnormal server-initiated closure
        this.emitEvent({
          type: TransportEventType.Closed,
          code: WebSocketTransport.CLOSE_ABNORMAL,
          detail: "Abnormal closure"
        });
      } else if (
        previousState !== WebSocketConnectionState.CLOSING &&
        event.code !== 1000
      ) {
        // Peer-initiated close (was not in CLOSING state when onclose fired)
        this.emitEvent({
          type: TransportEventType.Closed,
          code: WebSocketTransport.CLOSE_PEER_INITIATED,
          detail: "Normal closure initiated by the peer"
        });
      } else {
        this.emitEvent({
          type: TransportEventType.Closed,
          code: event.code,
          detail: event.reason || "Connection closed"
        });
      }

      this.mSocket = null;
    };
  }

  /**
   * Schedule draining one message from the ring buffer per microtask.
   * This ensures one message per write-ready callback (REQ-TRANS-09).
   */
  private scheduleDrain(): void {
    if (this.mIsDrainScheduled || this.mBufferCount === 0) {
      return;
    }

    this.mIsDrainScheduled = true;
    Promise.resolve().then((): void => {
      this.mIsDrainScheduled = false;
      this.drainOne();

      // Schedule next if more pending
      if (this.mBufferCount > 0) {
        this.scheduleDrain();
      }
    });
  }

  /**
   * Drain one message from the ring buffer and send it over the wire.
   *
   * @returns true if a message was drained, false if unable to drain
   */
  private drainOne(): boolean {
    if (
      this.mBufferCount === 0 ||
      this.mSocket === null ||
      this.mSocket.readyState !== WebSocket.OPEN
    ) {
      return false;
    }

    const entry: IOutboundBufferEntry | null =
      this.mOutboundBuffer[this.mBufferHead];
    if (entry === null) {
      return false;
    }

    try {
      this.mSocket.send(entry.frame);
    } catch (error: unknown) {
      Log.e(
        WebSocketTransport.TAG,
        "drainOne(): WebSocket.send() failed",
        error
      );
    }

    this.mOutboundBuffer[this.mBufferHead] = null;
    this.mBufferHead = (this.mBufferHead + 1) % this.mBufferCapacity;
    this.mBufferCount--;
    return true;
  }

  /**
   * Drain all pending messages synchronously.
   * Used before sending the close frame (REQ-TRANS-09).
   * Breaks if drainOne() cannot make progress to avoid infinite loops.
   */
  private drainAll(): void {
    while (this.mBufferCount > 0) {
      if (!this.drainOne()) {
        this.resetBuffer();
        break;
      }
    }
  }

  private resetBuffer(): void {
    this.mOutboundBuffer.fill(null);
    this.mBufferHead = 0;
    this.mBufferTail = 0;
    this.mBufferCount = 0;
    this.mIsDrainScheduled = false;
  }

  private forceCloseSocket(): void {
    if (this.mSocket !== null) {
      try {
        this.mSocket.onopen = null;
        this.mSocket.onmessage = null;
        this.mSocket.onerror = null;
        this.mSocket.onclose = null;
        this.mSocket.close();
      } catch (error: unknown) {
        Log.w(
          WebSocketTransport.TAG,
          "forceCloseSocket(): Error during force-close",
          error
        );
      }
      this.mSocket = null;
    }
  }

  private emitEvent(event: TransportEvent): void {
    for (const listener of this.mEventListeners) {
      try {
        listener(event);
      } catch (error: unknown) {
        Log.e(
          WebSocketTransport.TAG,
          `emitEvent(): Listener threw for event ${event.type}`,
          error
        );
      }
    }
  }

  /**
   * Get the current connection state (REQ-TRANS-07).
   */
  public getState(): WebSocketConnectionState {
    return this.mState;
  }

  /**
   * Register an event listener for transport events.
   */
  public addEventListener(listener: TransportEventListener): void {
    this.mEventListeners.push(listener);
  }

  /**
   * Remove an event listener.
   */
  public removeEventListener(listener: TransportEventListener): void {
    this.mEventListeners = this.mEventListeners.filter(
      (existing) => existing !== listener
    );
  }

  /**
   * Connect to the WSS endpoint using the native WebSocket API.
   *
   * @param url - The full wss:// URL including JWT authorization query parameter
   * @param subProtocol - The sub-protocol to negotiate (default: "com.tivo.mindrpc.2")
   */
  public connect(
    url: string,
    subProtocol: string = "com.tivo.mindrpc.2"
  ): void {
    if (this.mState !== WebSocketConnectionState.DISCONNECTED) {
      Log.w(
        WebSocketTransport.TAG,
        `connect(): Cannot connect while in state ${this.mState}`
      );
      return;
    }

    // REQ-WEB-TRANS-01: Enforce wss:// only
    if (!url.startsWith("wss://")) {
      this.emitEvent({
        type: TransportEventType.Error,
        code: WebSocketErrorCode.TRANSPORT_ERROR,
        detail: "Only wss:// connections are allowed"
      });
      return;
    }

    Log.d(WebSocketTransport.TAG, "connect(): Opening WebSocket connection");
    this.mState = WebSocketConnectionState.CONNECTING;

    try {
      this.mSocket = new WebSocket(url, [subProtocol]);
      this.mSocket.binaryType = "arraybuffer";
      this.attachSocketListeners();
    } catch (error: unknown) {
      Log.e(
        WebSocketTransport.TAG,
        "connect(): WebSocket constructor failed",
        error
      );
      this.mState = WebSocketConnectionState.DISCONNECTED;
      this.emitEvent({
        type: TransportEventType.Error,
        code: WebSocketErrorCode.TRANSPORT_ERROR,
        detail: `WebSocket constructor failed: ${error instanceof Error ? error.message : String(error)}`
      });
    }
  }

  /**
   * Send a text frame over the active connection (REQ-TRANS-04).
   *
   * Messages are queued in the ring buffer and drained one per write-ready callback.
   *
   * @param frame - The raw text frame to send
   * @throws WebSocketError if not connected or buffer is full
   */
  public send(frame: string): void {
    if (this.mState !== WebSocketConnectionState.CONNECTED) {
      throw new WebSocketError(
        WebSocketErrorCode.NOT_CONNECTED,
        `Cannot send: connection state is ${this.mState}`
      );
    }

    if (this.mBufferCount >= this.mBufferCapacity) {
      throw new WebSocketError(
        WebSocketErrorCode.BUFFER_FULL,
        `Outbound ring buffer is full (capacity: ${this.mBufferCapacity})`
      );
    }

    // Enqueue in ring buffer
    this.mOutboundBuffer[this.mBufferTail] = {
      frame: frame,
      timestamp: Date.now()
    };
    this.mBufferTail = (this.mBufferTail + 1) % this.mBufferCapacity;
    this.mBufferCount++;

    this.scheduleDrain();
  }

  /**
   * Graceful close (REQ-TRANS-05, REQ-TRANS-11).
   *
   * @param code - RFC 6455 close code (default: 1000 = normal closure)
   * @param reason - Reason phrase (default: "Normal closure")
   */
  public close(code: number = 1000, reason: string = "Normal closure"): void {
    // REQ-TRANS-11: Close guard
    if (
      this.mState === WebSocketConnectionState.DISCONNECTED ||
      this.mState === WebSocketConnectionState.CLOSING
    ) {
      this.emitEvent({
        type: TransportEventType.Closed,
        code: WebSocketTransport.CLOSE_ALREADY_DISCONNECTING,
        detail: "Failed to close: already disconnecting or disconnected"
      });
      return;
    }

    if (this.mState === WebSocketConnectionState.CONNECTING) {
      Log.d(
        WebSocketTransport.TAG,
        "close(): Force-cancelling in-progress connection"
      );
      this.mState = WebSocketConnectionState.CLOSING;
      this.forceCloseSocket();
      this.emitEvent({
        type: TransportEventType.Closed,
        code: WebSocketTransport.CLOSE_FORCE_CANCEL,
        detail: "Force cancel the connection"
      });
      this.mState = WebSocketConnectionState.DISCONNECTED;
      return;
    }

    if (this.mSocket === null) {
      this.emitEvent({
        type: TransportEventType.Closed,
        code: WebSocketTransport.CLOSE_NO_ACTIVE_CONNECTION,
        detail: "Failed to close: no active connections"
      });
      return;
    }

    Log.d(
      WebSocketTransport.TAG,
      `close(): Initiating graceful close with code=${code}`
    );

    this.mState = WebSocketConnectionState.CLOSING;

    // REQ-TRANS-09: Drain pending messages before sending close frame
    this.drainAll();

    try {
      this.mSocket.close(code, reason);
    } catch (error: unknown) {
      Log.e(WebSocketTransport.TAG, "close(): WebSocket.close() failed", error);
      this.forceCloseSocket();
      this.mState = WebSocketConnectionState.DISCONNECTED;
      this.emitEvent({
        type: TransportEventType.Closed,
        code: WebSocketTransport.CLOSE_ABNORMAL,
        detail: "Abnormal closure"
      });
    }
  }

  /**
   * Destroy the transport, releasing all resources.
   */
  public destroy(): void {
    this.forceCloseSocket();
    this.mEventListeners = [];
    this.resetBuffer();
    this.mState = WebSocketConnectionState.DISCONNECTED;
    Log.d(WebSocketTransport.TAG, "destroy(): Transport destroyed");
  }

  /**
   * Get pending outbound message count.
   */
  public getPendingCount(): number {
    return this.mBufferCount;
  }
}
